import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
} from '@mui/material';
import {
  useCreateExpense,
  useExpenseSubcategories,
  useGetLegalEntities,
  useGetTaxCodesByCountry,
} from '@pflegenavi/frontend/api-nursing-home';
import { createInternalId, draftsToUpdateParts } from './expensePartsDrafts';
import { ExpensePartsEditor, type PartDraft } from './ExpensePartsEditor';
import { useNursingHomeContext } from '@pflegenavi/frontend/nursing-home-context';
import { useFormatting } from '@pflegenavi/frontend/localization';
import { useSnackbar } from 'notistack';
import {
  FDatePicker,
  FTextInput,
  Typography,
  useErrorSnackbar,
} from '@pflegenavi/web-components';
import { format } from 'date-fns';
import type { FC } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  UploadReceiptCard,
  useUploadReceipt,
} from '@pflegenavi/transaction-components';

interface FormValues {
  receiptImageIds: string[];
  receipt_not_available: boolean;
  receiptDate: Date | null;
  receiptNumber: string;
  comment: string;
  parts: PartDraft[];
}

export interface AssignToExpenseManagementModalProps {
  open: boolean;
  onClose: () => void;
  /** Cash transaction group to assign — caller must ensure amount_linked === 0 */
  cashTransactionGroupId: string;
  /** Absolute group amount in cents */
  groupAmountCents: number;
  /** Shown in the context block at the top of the dialog. */
  merchantName?: string | null;
  /** Shown in the context block at the top of the dialog. */
  cashListName?: string | null;
  description?: React.ReactNode;
  onSuccess?: () => void;
}

function buildValidationSchema(groupAmount: number) {
  return Yup.object({
    receiptImageIds: Yup.array().of(Yup.string().required()),
    receipt_not_available: Yup.boolean().required(),
    receiptDate: Yup.date().required(),
    receiptNumber: Yup.string(),
    comment: Yup.string(),
    parts: Yup.array().test(
      'parts-sum-matches-group',
      'parts-sum-mismatch',
      (parts) => sumPartAmounts(parts) === groupAmount
    ),
  });
}

function sumPartAmounts(parts: PartDraft[] | undefined): number {
  if (!parts) {
    return 0;
  }
  return parts.reduce((acc, p) => acc + (p.amount || 0), 0);
}

export const AssignToExpenseManagementModal: FC<
  AssignToExpenseManagementModalProps
> = ({
  open,
  onClose,
  cashTransactionGroupId,
  groupAmountCents,
  merchantName,
  cashListName,
  description,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { fCurrencyCents } = useFormatting();
  const { enqueueSnackbar } = useSnackbar();
  const { enqueueErrorSnackbar } = useErrorSnackbar();
  const { mutateAsync: createExpense } = useCreateExpense();
  const { selectedNursingHome } = useNursingHomeContext();

  const { data: subcategoriesData } = useExpenseSubcategories({
    enabled: open,
  });
  const { data: legalEntitiesData } = useGetLegalEntities({ enabled: open });
  const countryId = useMemo(() => {
    if (!selectedNursingHome?.legalEntityId || !legalEntitiesData) {
      return undefined;
    }
    const le = legalEntitiesData.data.find(
      (l) => l.id === selectedNursingHome.legalEntityId
    );
    return le?.base_data?.country_id ?? undefined;
  }, [selectedNursingHome?.legalEntityId, legalEntitiesData]);
  const { data: taxCodesData } = useGetTaxCodesByCountry(countryId);

  const initialValues = useMemo<FormValues>(
    () => ({
      receiptImageIds: [],
      receipt_not_available: false,
      receiptDate: new Date(),
      receiptNumber: '',
      comment: '',
      parts: [
        {
          _internalId: createInternalId(),
          amount: groupAmountCents,
          expense_subcategory_id: null,
          expense_part_tax: null,
        },
      ],
    }),
    // Reset the form when the caller switches to a different target row.
    // cashTransactionGroupId isn't read in the body — it's here purely as a
    // reset trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cashTransactionGroupId, groupAmountCents]
  );

  const validationSchema = useMemo(
    () => buildValidationSchema(groupAmountCents),
    [groupAmountCents]
  );

  const formik = useFormik<FormValues>({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values, { setSubmitting }) => {
      if (!values.receiptDate) {
        return;
      }
      try {
        await createExpense({
          body: {
            cash_list_transaction_group_id: cashTransactionGroupId,
            expense_parts: draftsToUpdateParts(values.parts),
            comment:
              values.comment.trim() === '' ? null : values.comment.trim(),
            receipt_date: format(values.receiptDate, 'yyyy-MM-dd'),
            receipt_number:
              values.receiptNumber.trim() === ''
                ? null
                : values.receiptNumber.trim(),
            receipt_image_ids: values.receiptImageIds,
            receipt_not_available: values.receipt_not_available,
          },
        });

        enqueueSnackbar(
          t('receipts.unassociatedPliant.assignExpense.success'),
          { variant: 'success' }
        );
        onSuccess?.();
        onClose();
      } catch (error) {
        enqueueErrorSnackbar(error, t('errors.something-went-wrong'));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const uploadReceipt = useUploadReceipt({ formik });

  const partsSumMatches =
    sumPartAmounts(formik.values.parts) === groupAmountCents;

  const canConfirm =
    formik.values.receiptDate !== null &&
    !formik.isSubmitting &&
    partsSumMatches &&
    (formik.values.receipt_not_available ||
      formik.values.receiptImageIds.length > 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('receipts.unassociatedPliant.assignExpense.title')}
      </DialogTitle>
      <DialogContent dividers>
        {description && <Box sx={{ mb: 2 }}>{description}</Box>}

        <Stack spacing={1.5} sx={{ mb: 2 }}>
          <ContextRow
            label={t('accounting.expenses.dialog.context.cash-list')}
            value={cashListName ?? '--'}
          />
          <ContextRow
            label={t('accounting.expenses.dialog.context.amount')}
            value={fCurrencyCents(groupAmountCents)}
          />
          <ContextRow
            label={t('accounting.expenses.dialog.context.merchant')}
            value={merchantName ?? '--'}
          />
        </Stack>
        <Divider sx={{ mb: 2 }} />

        <Stack spacing={2}>
          <FDatePicker
            name="receiptDate"
            formik={formik}
            label={t('receipts.unassociatedPliant.assignExpense.receiptDate')}
          />

          <FTextInput
            name="receiptNumber"
            formik={formik}
            label={t('receipts.unassociatedPliant.assignExpense.receiptNumber')}
            fullWidth
          />

          <ExpensePartsEditor
            parts={formik.values.parts}
            onChange={(parts) => formik.setFieldValue('parts', parts)}
            groupAmount={groupAmountCents}
            subcategories={subcategoriesData?.data ?? []}
            taxCodes={taxCodesData?.data ?? []}
            errorMessage={
              typeof formik.errors.parts === 'string' &&
              formik.errors.parts !== 'parts-sum-mismatch'
                ? formik.errors.parts
                : null
            }
          />

          <UploadReceiptCard
            formik={formik}
            uploadReceipt={uploadReceipt}
            disabled={false}
            allowWithoutImage
          />

          <FTextInput
            name="comment"
            formik={formik}
            label={t('receipts.unassociatedPliant.assignExpense.comment')}
            fullWidth
            multiline
            minRows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          color="inherit"
          disabled={formik.isSubmitting}
        >
          {t('actions.cancel')}
        </Button>
        <Button
          onClick={() => formik.handleSubmit()}
          color="primary"
          variant="contained"
          disabled={!canConfirm}
        >
          {t('receipts.unassociatedPliant.assignExpense.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface ContextRowProps {
  label: string;
  value: React.ReactNode;
}

const ContextRow: FC<ContextRowProps> = ({ label, value }) => (
  <Stack direction="row" spacing={1} alignItems="baseline">
    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 110 }}>
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Stack>
);
